// ============================================================================
// RBAC permission catalog + role matrix.
//
// scope:
//   'tenant' = applies within a business's own account (a Client/business_owner
//              can have it — it only ever touches THEIR own data/team).
//   'system' = platform-wide / cross-tenant / impersonation — Super Admin only.
//
// Super Admin (super_admin/admin) gets EVERY permission.
// Client (business_owner) gets every 'tenant' permission.
// Other roles get curated subsets (see permissionsForRole).
// ============================================================================

export const MODULES = [
  {
    key: 'clients', label: 'Gestión de clientes', permissions: [
      { key: 'clients.view', label: 'Ver todos los clientes', scope: 'tenant' },
      { key: 'clients.create', label: 'Crear nuevos clientes', scope: 'tenant' },
      { key: 'clients.edit', label: 'Editar detalles de cliente', scope: 'tenant' },
      { key: 'clients.delete', label: 'Eliminar / desactivar clientes', scope: 'tenant' },
      { key: 'clients.search', label: 'Buscar y filtrar clientes', scope: 'tenant' },
      { key: 'clients.history', label: 'Ver historial y actividad del cliente', scope: 'tenant' },
      { key: 'clients.impersonate', label: 'Impersonar / acceder como cliente', scope: 'system' },
    ],
  },
  {
    key: 'billing', label: 'Facturación', permissions: [
      { key: 'billing.view', label: 'Ver todas las facturas', scope: 'tenant' },
      { key: 'billing.create', label: 'Crear facturas', scope: 'tenant' },
      { key: 'billing.edit', label: 'Editar facturas', scope: 'tenant' },
      { key: 'billing.void', label: 'Cancelar / anular facturas', scope: 'tenant' },
      { key: 'billing.download', label: 'Descargar PDFs de facturas', scope: 'tenant' },
      { key: 'billing.status', label: 'Ver estado (pagada/impagada/vencida)', scope: 'tenant' },
      { key: 'billing.recurring', label: 'Gestionar facturas recurrentes', scope: 'tenant' },
      { key: 'billing.export', label: 'Exportar datos de facturación', scope: 'tenant' },
    ],
  },
  {
    key: 'orders', label: 'Pedidos', permissions: [
      { key: 'orders.view', label: 'Ver pedidos del equipo', scope: 'tenant' },
      { key: 'orders.create', label: 'Tomar / crear pedidos', scope: 'tenant' },
      { key: 'orders.convert', label: 'Convertir pedidos en factura / albarán', scope: 'tenant' },
      { key: 'orders.delete', label: 'Eliminar pedidos', scope: 'tenant' },
    ],
  },
  {
    key: 'delivery', label: 'Reparto', permissions: [
      { key: 'delivery.view', label: 'Ver repartos del equipo', scope: 'tenant' },
      { key: 'delivery.assign', label: 'Asignar repartidor y preparar repartos', scope: 'tenant' },
      { key: 'delivery.drive', label: 'Repartir y marcar entregas (repartidor)', scope: 'tenant' },
    ],
  },
  {
    // Catálogos de productos — module gated by the product_catalogue feature
    // flag; mirrored server side by catalogue_can() in backend/product_catalogues.sql.
    key: 'catalogue', label: 'Catálogos de productos', permissions: [
      { key: 'catalogue.view', label: 'Ver catálogos', scope: 'tenant' },
      { key: 'catalogue.create', label: 'Crear catálogos', scope: 'tenant' },
      { key: 'catalogue.edit', label: 'Editar catálogos', scope: 'tenant' },
      { key: 'catalogue.delete', label: 'Eliminar catálogos', scope: 'tenant' },
      { key: 'catalogue.publish', label: 'Publicar / despublicar catálogos', scope: 'tenant' },
    ],
  },
  {
    key: 'finance', label: 'Datos financieros', permissions: [
      { key: 'finance.viewIncomeExpenses', label: 'Ver ingresos y gastos', scope: 'tenant' },
      { key: 'finance.viewPayments', label: 'Ver registros de pago', scope: 'tenant' },
      { key: 'finance.viewTax', label: 'Ver información de impuestos / IVA', scope: 'tenant' },
      { key: 'finance.reports', label: 'Acceder a informes financieros', scope: 'tenant' },
      { key: 'finance.export', label: 'Exportar datos contables', scope: 'tenant' },
      { key: 'finance.balances', label: 'Ver saldos y pendientes de clientes', scope: 'tenant' },
    ],
  },
  {
    key: 'contacts', label: 'Clientes y proveedores', permissions: [
      { key: 'contacts.viewCustomers', label: 'Ver todos los clientes', scope: 'tenant' },
      { key: 'contacts.manageCustomers', label: 'Crear/editar/eliminar clientes', scope: 'tenant' },
      { key: 'contacts.viewSuppliers', label: 'Ver todos los proveedores', scope: 'tenant' },
      { key: 'contacts.manageSuppliers', label: 'Crear/editar/eliminar proveedores', scope: 'tenant' },
      { key: 'contacts.importExport', label: 'Importar/exportar listas de contactos', scope: 'tenant' },
    ],
  },
  {
    key: 'documents', label: 'Documentos', permissions: [
      { key: 'documents.view', label: 'Ver documentos subidos', scope: 'tenant' },
      { key: 'documents.upload', label: 'Subir documentos', scope: 'tenant' },
      { key: 'documents.editMeta', label: 'Editar metadatos de documentos', scope: 'tenant' },
      { key: 'documents.download', label: 'Descargar documentos', scope: 'tenant' },
      { key: 'documents.delete', label: 'Eliminar documentos', scope: 'tenant' },
      { key: 'documents.archive', label: 'Acceder a archivos archivados', scope: 'tenant' },
    ],
  },
  {
    key: 'users', label: 'Usuarios y accesos', permissions: [
      { key: 'users.create', label: 'Crear usuarios (de su equipo)', scope: 'tenant' },
      { key: 'users.edit', label: 'Editar usuarios', scope: 'tenant' },
      { key: 'users.toggle', label: 'Activar/desactivar usuarios', scope: 'tenant' },
      { key: 'users.resetPassword', label: 'Restablecer contraseñas', scope: 'tenant' },
      { key: 'users.assignRoles', label: 'Asignar roles', scope: 'tenant' },
      { key: 'users.remove', label: 'Eliminar usuarios', scope: 'tenant' },
      { key: 'users.permissions', label: 'Gestionar permisos', scope: 'tenant' },
      { key: 'users.loginHistory', label: 'Ver historial de accesos', scope: 'tenant' },
    ],
  },
  {
    key: 'company', label: 'Configuración de empresa', permissions: [
      { key: 'company.view', label: 'Ver perfil de empresa', scope: 'tenant' },
      { key: 'company.edit', label: 'Editar información de empresa', scope: 'tenant' },
      { key: 'company.tax', label: 'Gestionar configuración fiscal', scope: 'tenant' },
      { key: 'company.templates', label: 'Gestionar plantillas de factura', scope: 'tenant' },
      { key: 'company.branding', label: 'Configurar marca / logo', scope: 'tenant' },
      { key: 'company.email', label: 'Gestionar configuración de email', scope: 'tenant' },
      { key: 'company.integrations', label: 'Gestionar integraciones y API', scope: 'tenant' },
    ],
  },
  {
    key: 'reports', label: 'Informes y analítica', permissions: [
      { key: 'reports.dashboards', label: 'Ver todos los paneles', scope: 'tenant' },
      { key: 'reports.clientReports', label: 'Ver informes por cliente', scope: 'tenant' },
      { key: 'reports.custom', label: 'Generar informes personalizados', scope: 'tenant' },
      { key: 'reports.export', label: 'Exportar informes (Excel/PDF/CSV)', scope: 'tenant' },
      { key: 'reports.metrics', label: 'Ver métricas de rendimiento', scope: 'tenant' },
    ],
  },
  {
    key: 'audit', label: 'Auditoría y seguridad', permissions: [
      { key: 'audit.logs', label: 'Ver registros de auditoría', scope: 'tenant' },
      { key: 'audit.allActions', label: 'Ver acciones de todos los usuarios', scope: 'tenant' },
      { key: 'audit.loginActivity', label: 'Monitorizar actividad de acceso', scope: 'tenant' },
      { key: 'audit.security', label: 'Gestionar ajustes de seguridad', scope: 'tenant' },
      { key: 'audit.mfa', label: 'Activar/desactivar MFA (2FA)', scope: 'tenant' },
      { key: 'audit.ipRestrictions', label: 'Gestionar restricciones de IP', scope: 'tenant' },
      { key: 'audit.compliance', label: 'Acceder a registros de cumplimiento', scope: 'tenant' },
    ],
  },
  {
    key: 'support', label: 'Soporte y administración', permissions: [
      { key: 'support.ownTickets', label: 'Ver/crear sus propios tickets', scope: 'tenant' },
      { key: 'support.communications', label: 'Ver su historial de comunicaciones', scope: 'tenant' },
      { key: 'support.ownSubscription', label: 'Gestionar su propia suscripción/plan', scope: 'tenant' },
      { key: 'support.allTickets', label: 'Acceder a TODOS los tickets de soporte', scope: 'system' },
      { key: 'support.ticketsForClients', label: 'Crear tickets en nombre de clientes', scope: 'system' },
      { key: 'support.manageSubscriptions', label: 'Gestionar suscripciones/planes de clientes', scope: 'system' },
      { key: 'support.toggleServices', label: 'Activar/desactivar servicios', scope: 'system' },
    ],
  },
  {
    key: 'superadmin', label: 'Exclusivo Super Admin', permissions: [
      { key: 'super.allAccounts', label: 'Acceso total a cada cuenta de cliente', scope: 'system' },
      { key: 'super.allModules', label: 'Acceso total a cada módulo', scope: 'system' },
      { key: 'super.manageAllUsers', label: 'Gestionar todos los usuarios y roles', scope: 'system' },
      { key: 'super.allFinancial', label: 'Acceso a toda la información financiera/facturación', scope: 'system' },
      { key: 'super.override', label: 'Anular restricciones de permisos', scope: 'system' },
      { key: 'super.systemConfig', label: 'Configuración global del sistema', scope: 'system' },
      { key: 'super.exportAll', label: 'Exportar todos los datos del sistema', scope: 'system' },
      { key: 'super.transferOwnership', label: 'Transferir propiedad / derechos de admin', scope: 'system' },
      { key: 'super.allDocuments', label: 'Acceder a documentos e informes de todos', scope: 'system' },
    ],
  },
];

export const ALL_PERMISSIONS = MODULES.flatMap(m => m.permissions);

// Tenant-scoped permissions a manager / accountant / employee may receive.
// Delivery is admin/owner-only: only the account admin assigns drivers and
// manages repartos. A manager does NOT get delivery by default — the owner can
// still grant delivery.view/assign per-user (effectivePermissions) if they want.
const MANAGER_EXCLUDE = ['users.remove', 'users.permissions', 'audit.security', 'audit.ipRestrictions', 'company.integrations',
  'delivery.view', 'delivery.assign', 'delivery.drive'];
const ACCOUNTANT_INCLUDE = ['billing.', 'finance.', 'reports.', 'documents.view', 'documents.download', 'contacts.view', 'clients.view'];
const EMPLOYEE_INCLUDE = ['billing.view', 'billing.create', 'documents.view', 'documents.upload', 'clients.view', 'contacts.view', 'catalogue.view'];
// Comercial (field sales rep): take orders for clients, see the catalogue, apply
// tariffs. By default they ONLY work with pedidos — they cannot create invoices.
// The owner can grant extra permissions per member (see effectivePermissions).
const COMERCIAL_INCLUDE = ['orders.view', 'orders.create', 'clients.view', 'clients.create', 'clients.search', 'clients.history', 'contacts.viewCustomers', 'documents.view', 'catalogue.view'];
// Repartidor (delivery driver): sees ONLY their assigned delivery queue and the
// customer's address/phone, and can mark deliveries done (with payment + notes).
// They cannot take orders or touch billing. Owner can grant extras per member.
const DRIVER_INCLUDE = ['delivery.view', 'delivery.drive', 'clients.view'];

export function permissionsForRole(role) {
  const tenant = ALL_PERMISSIONS.filter(p => p.scope === 'tenant').map(p => p.key);
  switch (role) {
    case 'super_admin':
    case 'admin':
      return ALL_PERMISSIONS.map(p => p.key);          // everything
    case 'business_owner':
      return tenant;                                    // full control of own tenant
    case 'manager':
      return tenant.filter(k => !MANAGER_EXCLUDE.includes(k));
    case 'accountant':
      return tenant.filter(k => ACCOUNTANT_INCLUDE.some(pre => k.startsWith(pre)));
    case 'employee':
      return EMPLOYEE_INCLUDE.slice();
    case 'comercial':
      return COMERCIAL_INCLUDE.slice();
    case 'repartidor':
      return DRIVER_INCLUDE.slice();
    default:
      return [];
  }
}

export function roleHasPermission(role, key) {
  return permissionsForRole(role).includes(key);
}

// A signup default of 'user' (or a missing role) is the account owner.
export function normalizeRole(role) {
  return (!role || role === 'user') ? 'business_owner' : role;
}

// A member's effective permissions = their role baseline PLUS any extra keys the
// owner granted them per-user (stored in profiles.permissions). Owners grant on
// top of the role; the role is always the floor.
export function effectivePermissions(role, extra) {
  const base = permissionsForRole(normalizeRole(role));
  const grants = Array.isArray(extra) ? extra : [];
  return grants.length ? Array.from(new Set([...base, ...grants])) : base;
}

// The landing page a member is allowed to see, used as a redirect target when
// they hit a page they lack permission for. Must always be a page they CAN open
// (otherwise the route guard would loop). A comercial locked to pedidos lands on
// /orders; everyone else falls back to the dashboard.
export function homePathForPerms(perms) {
  const has = (k) => Array.isArray(perms) && perms.includes(k);
  if (has('reports.dashboards') || has('billing.view')) return '/dashboard';
  if (has('orders.view')) return '/orders';
  // A driver locked to deliveries lands on their queue.
  if (has('delivery.drive') || has('delivery.view')) return '/deliveries';
  return '/dashboard';
}

// ---------------------------------------------------------------------------
// i18n. The Spanish label baked into MODULES / ROLE_LABELS above stays the
// canonical fallback (same pattern as lib/documentDesign.js), so nothing breaks
// if a key is missing; the `perms.*` locale namespace supplies the translation.
// Team Management renders these, which is why an English (Pakistan) account
// used to see Spanish permission text on an otherwise English screen.
//
//   const { t } = useTranslation();
//   moduleLabel(mod, t)  permissionLabel(perm, t)  roleLabel('manager', t)
// ---------------------------------------------------------------------------
export function moduleLabel(mod, t) {
  if (!mod) return '';
  return t ? t(`perms.mod_${mod.key}`, { defaultValue: mod.label }) : mod.label;
}

export function permissionLabel(perm, t) {
  if (!perm) return '';
  return t ? t(`perms.p_${perm.key}`, { defaultValue: perm.label }) : perm.label;
}

export function roleLabel(role, t) {
  const fallback = ROLE_LABELS[role] || role || '';
  return t ? t(`perms.role_${role}`, { defaultValue: fallback }) : fallback;
}

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  business_owner: 'Cliente (Propietario)',
  manager: 'Gestor',
  accountant: 'Contable',
  employee: 'Empleado',
  comercial: 'Comercial',
  repartidor: 'Repartidor',
};
